/** Keep in sync with lib/kenyanRegistrationValidationCjs.js */

export function normalizeKenyanRegistration(reg) {
  return String(reg == null ? '' : reg)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

/** Tractor TPO: KTC prefix, at least 4 characters, no maximum. */
export const MIN_TRACTOR_PLATE_LENGTH = 4;

/** Practical Kenyan plate floor: older cars are 6 chars; current/motorcycle are longer. */
export const MIN_PLATE_LENGTH = 6;
/** UI/input ceiling for normal plates (chassis-as-reg can be longer). */
export const MAX_PLATE_LENGTH = 10;

function looksLikeChassisAsRegistration(cleaned) {
  if (!cleaned || cleaned.length < 8 || cleaned.length > 20) return false;
  if (!/\d/.test(cleaned)) return false;
  if (cleaned.length <= MAX_PLATE_LENGTH && /^K[A-Z0-9]+$/.test(cleaned)) return false;
  return true;
}

function incompletePlateMessage() {
  return 'Enter the full registration (at least 6 characters). Three-letter prefixes like KAV or KBM are not allowed.';
}

/** Tuk Tuk PSV / Commercial (and spellings like Tuktuk PSV). */
export function isTukTukVehicleClass(vehicleClass) {
  return /tuk\s*tuk|tuktuk/i.test(String(vehicleClass || ''));
}

/**
 * NTSA often returns make/body like PIAGGIO-TUKTUK even when the agent picked another class.
 * Temporary gate until non-KTW three-wheeler plates are accepted.
 */
export function looksLikeTukTukVehicle(details) {
  const blob = [details?.make, details?.model, details?.bodyType]
    .map((v) => String(v || '').toUpperCase())
    .join(' ');
  if (!blob.trim()) return false;
  return /TUK\s*TUK|TUKTUK|THREE\s*WHEELER|3[\s-]*WHEELER/.test(blob);
}

function tukTukKtwLabel(vehicleClass) {
  const vcls = String(vehicleClass || '').trim();
  if (/psv/i.test(vcls)) return 'Tuk Tuk PSV';
  if (/commercial/i.test(vcls)) return 'Tuk Tuk Commercial';
  return 'Tuk Tuk';
}

function tukTukKtwResult(cleaned, vehicleClass, extraMessage) {
  const label = tukTukKtwLabel(vehicleClass);
  return {
    valid: false,
    status: 'invalid',
    cleaned,
    message: extraMessage || `${label}: registration must start with KTW.`
  };
}

export function validateKenyanVehicleRegistration(reg, vehicleClass, options) {
  const opts = options || {};
  const cleaned = normalizeKenyanRegistration(reg);
  const vcls = String(vehicleClass || '').trim();
  const chassis = normalizeKenyanRegistration(opts.chassisNumber || '');
  const isNew =
    Boolean(opts.isNewVehicle) ||
    (Boolean(opts.allowChassisAsRegistration) && looksLikeChassisAsRegistration(cleaned)) ||
    (chassis.length >= 8 && cleaned === chassis);
  const tukTukFromClass = isTukTukVehicleClass(vcls);
  const tukTukFromNtsa = looksLikeTukTukVehicle(opts);
  const requireTukTukKtw = tukTukFromClass || tukTukFromNtsa;

  if (!cleaned) {
    return { valid: false, status: 'empty', cleaned, message: 'Enter the full vehicle registration number' };
  }

  if (vcls === 'Tractor TPO') {
    if (!cleaned.startsWith('KTC')) {
      return {
        valid: false,
        status: 'invalid',
        cleaned,
        message: 'Tractor TPO: registration must start with KTC (e.g. KTCC604K).'
      };
    }
    if (cleaned.length < MIN_TRACTOR_PLATE_LENGTH) {
      return {
        valid: false,
        status: 'invalid',
        cleaned,
        message: `Tractor TPO: registration must be at least ${MIN_TRACTOR_PLATE_LENGTH} characters.`
      };
    }
    return { valid: true, status: 'valid', cleaned, message: 'Valid KTC tractor plate' };
  }

  // Temporary: all tuk tuks (class or NTSA PIAGGIO-TUKTUK) must be KTW plates.
  if (requireTukTukKtw) {
    if (!cleaned.startsWith('KTW')) {
      return tukTukKtwResult(cleaned, vcls);
    }
    if (cleaned.length < MIN_PLATE_LENGTH || cleaned.length > MAX_PLATE_LENGTH || !/\d/.test(cleaned)) {
      return tukTukKtwResult(
        cleaned,
        vcls,
        `${tukTukKtwLabel(vcls)}: enter the full KTW registration (at least 6 characters, e.g. KTW123A).`
      );
    }
    return { valid: true, status: 'valid', cleaned, message: 'Valid KTW three-wheeler plate' };
  }

  if (isNew && looksLikeChassisAsRegistration(cleaned)) {
    return {
      valid: true,
      status: 'valid',
      cleaned,
      message: 'Chassis will be used as registration for this new vehicle'
    };
  }

  if (cleaned.length < MIN_PLATE_LENGTH) {
    return {
      valid: false,
      status: 'incomplete',
      cleaned,
      message: incompletePlateMessage()
    };
  }

  if (cleaned.length > MAX_PLATE_LENGTH && !looksLikeChassisAsRegistration(cleaned)) {
    return {
      valid: false,
      status: 'invalid',
      cleaned,
      message: `Registration is too long (max ${MAX_PLATE_LENGTH} characters for a number plate).`
    };
  }

  if (/motorcycle/i.test(vcls) && !cleaned.startsWith('KM')) {
    return {
      valid: false,
      status: 'invalid',
      cleaned,
      message: 'Motorcycle registration should start with KM (e.g. KMFB213R).'
    };
  }

  return {
    valid: true,
    status: 'valid',
    cleaned,
    message:
      cleaned.length === MIN_PLATE_LENGTH
        ? 'Valid registration (6 characters)'
        : 'Valid registration format'
  };
}

export function isRegistrationCompleteForNtsaLookup(reg, vehicleClass, options) {
  return validateKenyanVehicleRegistration(reg, vehicleClass, options || {}).valid;
}
